import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardBody, Col, Container, Row, Form, Label } from "reactstrap";
import logoLight from "../../assets/images/logo-light.png";
import { useFormik } from "formik";
import * as Yup from "yup";
import ParticlesAuth from "pages/AuthenticationInner/ParticlesAuth";
import { forgotPassword } from "Api/UserApi";
import { toast } from "react-toastify";
import BaseInput from "Components/Base/BaseInput";
import {
  InputPlaceHolder,
  otpRegex,
  otpTypeRegex,
  passwordRegex,
  validationMessages,
} from "Components/constants/validation";
import BaseButton from "Components/Base/BaseButton";
import ForgetPasswordFooter from "Components/Common/ForgetPasswordFooter";
import { loginLabels } from "Components/constants/Common";
import { HttpStatusCodes } from "helpers/Enums/Enums";
import {
  clearSessionStorage,
  getItem,
} from "Components/constants/SessionStores";

const ForgotPasswordPage = () => {
  document.title = loginLabels.forgetPassword;

  useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const navigate = useNavigate();

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (otpTypeRegex.test(value)) {
      validation.handleChange(e);
    }
  };

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      email: getItem(loginLabels.email) || "",
      otp: "",
      password: "",
      confirm_password: "",
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .required(validationMessages.required(loginLabels.OTP))
        .min(6, validationMessages.minLength(loginLabels.OTP, 6))
        .max(6, validationMessages.maxLength(loginLabels.OTP, 6))
        .matches(otpRegex, validationMessages.otpFormat(loginLabels.OTP)),
      password: Yup.string()
        .min(8, validationMessages.passwordLength(loginLabels.Password, 8))
        .matches(
          passwordRegex,
          validationMessages.passwordComplexity(loginLabels.Password)
        )
        .required(validationMessages.required(loginLabels.Password)),
      confirm_password: Yup.string()
        .oneOf(
          [Yup.ref(loginLabels.password), ""],
          validationMessages.passwordsMatch(
            loginLabels.Password,
            loginLabels.ConfirmPassword
          )
        )
        .required(validationMessages.required(loginLabels.ConfirmPassword)),
    }),

    onSubmit: async (values) => {
      setLoader(true);
      const payload = {
        email: values.email,
        otp: +values.otp,
        newPassword: values.password,
        confirmPassword: values.confirm_password,
      };

      try {
        const response = await forgotPassword(payload);
        if (
          response?.statusCode &&
          HttpStatusCodes.SUCCESS.includes(response?.statusCode)
        ) {
          toast.success(response.message);
          clearSessionStorage();
          navigate("/login");
        } else {
          toast.error(response.message);
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || error?.message);
      } finally {
        setLoader(false);
      }
    },
  });

  return (
    <ParticlesAuth>
      <div className="auth-page-content">
        <Container>
          <Row>
            <Col lg={12}>
              <div className="text-center mt-sm-5 mb-4 text-white-50">
                <div>
                  <Link to="/#" className="d-inline-block auth-logo">
                    <img src={logoLight} alt="" height="20" />
                  </Link>
                </div>
              </div>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card>
                <CardBody className="p-4">
                  <div className="text-center mt-2">
                    <h5 className="text-success">
                      {loginLabels.createNewPassword}
                    </h5>
                  </div>

                  <div className="p-2">
                    <Form
                      onSubmit={validation.handleSubmit}
                      action="/forget-password"
                    >
                      <div className="mb-3">
                        <BaseInput
                          name={loginLabels.otp}
                          label={loginLabels.OTP}
                          maxLength={6}
                          className="form-control"
                          placeholder={InputPlaceHolder(loginLabels.OTP)}
                          type="text"
                          onChange={handleOtpChange}
                          handleBlur={validation.handleBlur}
                          value={validation.values.otp || ""}
                          touched={validation.touched.otp}
                          error={validation.errors.otp}
                          required
                        />
                      </div>

                      <div className="mb-3">
                        <div className="position-relative auth-pass-inputgroup">
                          <BaseInput
                            type="password"
                            label={loginLabels.Password}
                            className="form-control pe-5 password-input"
                            placeholder={InputPlaceHolder(loginLabels.Password)}
                            name={loginLabels.password}
                            value={validation.values.password}
                            onChange={validation.handleChange}
                            handleBlur={validation.handleBlur}
                            touched={validation.touched.password}
                            error={validation.errors.password}
                            required
                          />
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="position-relative auth-pass-inputgroup mb-3">
                          <BaseInput
                            type="password"
                            label={loginLabels.ConfirmPassword}
                            className="form-control pe-5 password-input"
                            placeholder={InputPlaceHolder(
                              loginLabels.ConfirmPassword
                            )}
                            name={loginLabels.confirmPassword}
                            value={validation.values.confirm_password}
                            handleBlur={validation.handleBlur}
                            onChange={validation.handleChange}
                            touched={validation.touched.confirm_password}
                            error={validation.errors.confirm_password}
                            required
                          />
                        </div>
                      </div>

                      <div className="mt-4">
                        <BaseButton
                          color="success"
                          type="submit"
                          label={loginLabels.resetPassword}
                          loader={loader}
                          className="w-100"
                        />
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
              <ForgetPasswordFooter
                message={loginLabels.loginNavigateMessage}
                linkText={loginLabels.clickHere}
                linkUrl={"/login"}
              />
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

export default ForgotPasswordPage;
